import { useSelector } from "react-redux";
import type { IRootReducerState } from "@/store/IRootReducer";
import { useGetInstanceProductsQuery } from "@/store/api/instanceProductsApi";

export interface EmployeeGate {
  /** May this account open skyEmployee and select it as a landing page? */
  enabled: boolean;
  /** Is skyEmployee switched on for this instance? Drives the admin toggle. */
  enabledForInstance: boolean;
  /** Does this account control the toggle? */
  isSkypointAdmin: boolean;
  isLoading: boolean;
}

/**
 * Single source of truth for skyEmployee visibility. Consumed by the header
 * ProductSwitcher, the AppLayout employee branch, the gated redirect, and the
 * App Preferences page.
 *
 * The instance toggle is the ONLY authority: while it is off, skyEmployee is
 * invisible to everyone on that instance, a skypoint_admin included. An admin
 * previews the product by switching it on, not by being exempt — an exemption
 * meant the one account that decides whether to roll it out was the one account
 * that never saw what everybody else saw.
 *
 * `enabledForInstance` and `isSkypointAdmin` stay on the result so the App
 * Preferences toggle can still render and be operated while the product is off.
 * That control is what turns it back on, so it must never be gated on `enabled`.
 *
 * Deliberately NOT modelled on useWorkforceGate: WLM is admin-only by design,
 * whereas skyEmployee is available to all users of an instance once enabled.
 * Callers must not redirect away from /employee while isLoading is true.
 */
export function useEmployeeGate(): EmployeeGate {
  const { data, isLoading } = useGetInstanceProductsQuery();
  const isSkypointAdmin = useSelector(
    (state: IRootReducerState) => state.authReducer.isSkypointAdmin,
  );

  const enabledForInstance = data?.employee === true;

  return {
    enabled: !isLoading && enabledForInstance,
    enabledForInstance,
    isSkypointAdmin,
    isLoading,
  };
}
